// @generated-imports
import { applyFiltersAndRender, escapeHTML, findGameById, recomposeGames, showToast, slugify, updateContinueButton } from './app.js';
import { idbDatabaseExists } from './backup.js';
import { CONFIG, dom, state } from './core.js';
import { armButtonConfirm } from './datapanel.js';
import { isLayerOpen, layerClosed, layerOpened, openConfirmLayer, openPromptLayer } from './layers.js';
import { closePauseMenu } from './pausemenu.js';
import { getSetting, refreshSaveIndex } from './platform.js';
import { isOwnStateKey, profileStatePrefix, splitProfileIdentity } from './profiles.js';
import { renderResumeRow } from './resume.js';
import { currentSession, requestSessionState, sendSessionState, startSession } from './session.js';
import { bumpCounter } from './stats.js';
import { resetTimeline } from './timeline.js';
// @generated-imports
/* ===========================================================================
   SAVE STATES PERSISTENTES — "RETOMAR DE VERDADE" (v2.0 → v2.1)
   ---------------------------------------------------------------------------
   Banco `emutab-states` (v2):
     • store `states` — um registro por slot: `<identidade>:<slot>`, com
       bytes, miniatura, core, build, data e nome opcional;
     • store `meta` (v2.1) — índice LEVE, um registro por identidade com o
       estado mais recente (miniatura, slot, data). Alimenta Continuar,
       "Continue de onde parou", capa automática de ROM local e o selo 💾
       sem carregar megabytes de bytes. Migrado da v1 no upgrade.
   Identidade = `[<perfil>~]<gameId>|<core>` (perfil principal sem prefixo,
   compatível com a 2.0). Slots: `auto`, `previous`, `imported` e `1`..`10`
   manuais (quantidade visível em ⚙️). Carregar confere identidade e build.
   ======================================================================== */

const STATES_DB = 'emutab-states';
const STATES_STORE = 'states';
const STATES_META_STORE = 'meta';
const STATES_DB_VERSION = 2;
const MAX_MANUAL_SLOTS = 10;

function manualSlots() {
  const count = Math.min(MAX_MANUAL_SLOTS, Math.max(1, Number(getSetting('stateSlots')) || 3));
  return Array.from({ length: count }, (_, i) => String(i + 1));
}

function metaFromRecord(record) {
  return {
    id: record.identity,
    identity: record.identity,
    gameId: record.gameId,
    core: record.core,
    title: record.title,
    slot: record.slot,
    savedAt: record.savedAt,
    thumbnail: record.thumbnail || null,
    stateId: record.id,
  };
}

function openStatesDb() {
  return new Promise((resolve, reject) => {
    let request;
    try {
      request = indexedDB.open(STATES_DB, STATES_DB_VERSION);
    } catch (error) { reject(error); return; }
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STATES_STORE)) {
        const store = db.createObjectStore(STATES_STORE, { keyPath: 'id' });
        store.createIndex('identity', 'identity', { unique: false });
        store.createIndex('gameId', 'gameId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STATES_META_STORE)) {
        const meta = db.createObjectStore(STATES_META_STORE, { keyPath: 'id' });
        // v1 → v2: popula o índice a partir dos estados existentes.
        if (event.oldVersion >= 1) {
          const tx = request.transaction;
          const latest = new Map();
          tx.objectStore(STATES_STORE).openCursor().onsuccess = (cursorEvent) => {
            const cursor = cursorEvent.target.result;
            if (cursor) {
              const record = cursor.value;
              const prev = latest.get(record.identity);
              if (record.slot !== 'previous' && (!prev || (record.savedAt || 0) > (prev.savedAt || 0))) latest.set(record.identity, record);
              cursor.continue();
            } else {
              latest.forEach((record) => meta.put(metaFromRecord(record)));
            }
          };
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function stateIdentity(gameId, core) {
  return `${profileStatePrefix()}${gameId}|${core}`;
}

function stateRecordId(identity, slot) {
  return `${identity}:${slot}`;
}

async function statesTx(mode, work, stores = [STATES_STORE]) {
  const db = await openStatesDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(stores, mode);
      let result;
      try { result = work(...stores.map((name) => tx.objectStore(name))); } catch (error) { reject(error); return; }
      tx.oncomplete = () => resolve(result && typeof result.then === 'function' ? undefined : result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('abort'));
    });
  } finally {
    db.close();
  }
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getStateRecord(id) {
  const db = await openStatesDb();
  try {
    return await idbRequest(db.transaction(STATES_STORE, 'readonly').objectStore(STATES_STORE).get(id));
  } finally { db.close(); }
}

async function listStateRecords(identity) {
  const db = await openStatesDb();
  try {
    const store = db.transaction(STATES_STORE, 'readonly').objectStore(STATES_STORE);
    // Banco recriado por restauração de backup pode não ter o índice.
    const records = !identity ? await idbRequest(store.getAll())
      : store.indexNames && store.indexNames.contains('identity')
        ? await idbRequest(store.index('identity').getAll(identity))
        : (await idbRequest(store.getAll())).filter((r) => r && r.identity === identity);
    return (records || []).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  } finally { db.close(); }
}

async function listAllStateKeys() {
  try {
    const db = await openStatesDb();
    try {
      return await idbRequest(db.transaction(STATES_STORE, 'readonly').objectStore(STATES_STORE).getAllKeys());
    } finally { db.close(); }
  } catch (_) { return []; }
}

/** Grava um estado e atualiza o índice leve na MESMA transação. */
async function putStateRecord(record) {
  await statesTx('readwrite', (states, meta) => {
    states.put(record);
    if (record.slot !== 'previous') meta.put(metaFromRecord(record));
  }, [STATES_STORE, STATES_META_STORE]);
  updateStateMetaCache(metaFromRecord(record));
  return record;
}

async function deleteStateRecord(id) {
  const record = await getStateRecord(id).catch(() => null);
  await statesTx('readwrite', (states) => { states.delete(id); });
  if (record) await rebuildMetaForIdentity(record.identity);
}

/** Recalcula o índice de uma identidade após remoções. */
async function rebuildMetaForIdentity(identity) {
  const remaining = (await listStateRecords(identity)).filter((r) => r.slot !== 'previous');
  await statesTx('readwrite', (meta) => {
    if (remaining.length) meta.put(metaFromRecord(remaining[0]));
    else meta.delete(identity);
  }, [STATES_META_STORE]);
  await refreshStateMeta();
}

/** Apaga todos os estados cuja identidade começa com o prefixo (perfil). */
async function deleteStatesByPrefix(prefix) {
  await statesTx('readwrite', (states, meta) => {
    [states, meta].forEach((store) => {
      store.openCursor().onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) return;
        if (String(cursor.key).indexOf(prefix) === 0) cursor.delete();
        cursor.continue();
      };
    });
  }, [STATES_STORE, STATES_META_STORE]);
}

/* --- Índice em memória (perfil ativo) ----------------------------------- */

function updateStateMetaCache(meta) {
  if (!state.stateMeta) state.stateMeta = new Map();
  if (!meta || !isOwnStateKey(meta.identity)) return;
  const prev = state.stateMeta.get(meta.gameId);
  if (!prev || (meta.savedAt || 0) >= (prev.savedAt || 0)) state.stateMeta.set(meta.gameId, meta);
}

/** Carrega o índice leve do perfil ativo: gameId → estado mais recente. */
async function refreshStateMeta() {
  state.stateMeta = new Map();
  try {
    if (!(await idbDatabaseExists(STATES_DB))) return state.stateMeta;
    const db = await openStatesDb();
    try {
      const metas = await idbRequest(db.transaction(STATES_META_STORE, 'readonly').objectStore(STATES_META_STORE).getAll());
      (metas || []).forEach(updateStateMetaCache);
    } finally { db.close(); }
  } catch (_) { /* índice é opcional */ }
  return state.stateMeta;
}

function latestStateMeta(gameId) {
  return state.stateMeta ? state.stateMeta.get(gameId) || null : null;
}

/* --- Salvar / carregar ---------------------------------------------------- */

/** Pede o estado ao player da sessão e PERSISTE antes de dizer sucesso. */
async function saveSessionState(session, options = {}) {
  if (!session || !session.capabilities.state) return null;
  const kind = options.kind || 'manual';
  const slot = kind === 'auto' ? 'auto' : String(options.slot || '1');
  const data = await requestSessionState(session, options.timeoutMs || 6000);
  if (!data) return null;

  const identity = stateIdentity(session.gameId, session.core);
  const existingSlot = kind === 'manual' ? await getStateRecord(stateRecordId(identity, slot)).catch(() => null) : null;
  const record = {
    id: stateRecordId(identity, slot),
    identity,
    gameId: session.gameId,
    title: session.title,
    core: session.core,
    build: CONFIG.emulatorBuild,
    slot,
    name: existingSlot && existingSlot.name ? existingSlot.name : '',
    bytes: data.state instanceof Uint8Array ? data.state : new Uint8Array(data.state),
    thumbnail: typeof data.thumbnail === 'string' ? data.thumbnail : null,
    savedAt: Date.now(),
    appVersion: CONFIG.appVersion,
    profile: state.profileId || 'main',
  };

  try {
    if (slot === 'auto') {
      // O auto anterior vira "previous" — um passo atrás sempre recuperável.
      const existing = await getStateRecord(record.id);
      if (existing) {
        existing.id = stateRecordId(identity, 'previous');
        existing.slot = 'previous';
        await putStateRecord(existing);
      }
    }
    await putStateRecord(record);
  } catch (error) {
    if (kind === 'manual') showToast('Não deu para gravar o estado no navegador (espaço cheio?). O jogo continua.');
    return null;
  }
  session.lastSavedAt = record.savedAt;
  bumpCounter('stateSaves');
  if (dom.loadStateBtn) dom.loadStateBtn.classList.remove('is-hidden');
  refreshSaveIndex().then(() => applyFiltersAndRender());
  if (kind === 'manual' && !options.silent) showToast(`Estado salvo no ${describeSlot(slot)} — ${new Date(record.savedAt).toLocaleTimeString('pt-BR')}.`);
  if (session.kind === 'local') recomposeGames(); // capa automática pela miniatura
  updateContinueButton();
  renderResumeRow();
  return record;
}

/** Carrega um registro na sessão, checando identidade e compatibilidade. */
async function loadStateRecord(session, record, options = {}) {
  if (!session || !record || !session.capabilities.state) return false;
  const identity = stateIdentity(session.gameId, session.core);
  if (splitProfileIdentity(record.identity).rest !== splitProfileIdentity(identity).rest) {
    if (record.gameId !== session.gameId) {
      showToast(`Este estado é de "${record.title}", não de "${session.title}". Nada foi carregado.`);
      return false;
    }
    showToast(`Estado salvo com o core ${record.core}; a sessão usa ${session.core}. Nada foi carregado.`);
    return false;
  }
  const proceed = () => sendSessionState(session, record.bytes, 8000).then((ok) => {
    showToast(ok ? `Estado carregado (${describeSlot(record.slot, record.name)}).` : 'O emulador não aceitou o estado salvo.');
    if (ok) resetTimeline(session);
    return ok;
  });
  if (record.build && record.build !== CONFIG.emulatorBuild && !options.silent) {
    return new Promise((resolve) => {
      let answered = false;
      openConfirmLayer({
        title: 'Estado de outra build do emulador',
        message: `Este estado foi salvo com a build "${record.build}" e a sessão usa "${CONFIG.emulatorBuild}". Pode não ser compatível. Carregar mesmo assim?`,
        actions: [{ label: 'Carregar', handler: () => { answered = true; proceed().then(resolve); } }],
        cancelLabel: 'Não carregar',
      });
      const watcher = window.setInterval(() => {
        if (!isLayerOpen('confirm')) { window.clearInterval(watcher); if (!answered) resolve(false); }
      }, 300);
    });
  }
  return proceed();
}

function describeSlot(slot, name) {
  const base = slot === 'auto' ? 'automático'
    : slot === 'previous' ? 'anterior ao automático'
      : slot === 'imported' ? 'importado'
        : `slot ${slot}`;
  return name ? `${base} "${name}"` : base;
}

/* --- Botões do header: salvar/carregar rápido --------------------------- */

async function quickSaveCurrent() {
  const session = currentSession();
  if (!session || !session.capabilities.state) return;
  await saveSessionState(session, { kind: 'manual', slot: '1' });
}

async function quickLoadCurrent() {
  const session = currentSession();
  if (!session || !session.capabilities.state) return;
  const records = await listStateRecords(stateIdentity(session.gameId, session.core));
  const record = records.find((r) => r.slot === '1') || records.find((r) => r.slot === 'auto') || records[0];
  if (!record) { showToast('Nenhum estado salvo para este jogo ainda.'); return; }
  await loadStateRecord(session, record);
}

/* --- Painel de estados (slots) ------------------------------------------ */

async function openStatesPanel() {
  const session = currentSession();
  if (!dom.statesModal || !session) return;
  const records = await listStateRecords(stateIdentity(session.gameId, session.core));
  const bySlot = new Map(records.map((r) => [r.slot, r]));
  const canSave = session.capabilities.state;
  const rowFor = (slot, label, manual) => {
    const record = bySlot.get(slot);
    const meta = record
      ? `${new Date(record.savedAt).toLocaleString('pt-BR')} • build ${record.build || '?'}`
      : 'vazio';
    const thumb = record && record.thumbnail ? `<img class="state-thumb" src="${record.thumbnail}" alt="">` : '<span class="state-thumb state-thumb-empty" aria-hidden="true">—</span>';
    const title = record && record.name ? `${label} — ${record.name}` : label;
    return `<div class="backup-row data-row state-row">
      ${thumb}
      <span class="data-info"><strong>${escapeHTML(title)}</strong> <small>${escapeHTML(meta)}</small></span>
      <span class="data-actions">
        ${manual && canSave ? `<button class="mini-btn" type="button" data-state-save="${slot}">Salvar aqui</button>` : ''}
        <button class="mini-btn" type="button" data-state-load="${slot}"${record ? '' : ' disabled'}>Carregar</button>
        ${manual ? `<button class="mini-btn" type="button" data-state-name="${slot}"${record ? '' : ' disabled'}>Nomear</button>` : ''}
        <button class="mini-btn" type="button" data-state-send="${slot}"${record ? '' : ' disabled'} title="Exportar ou compartilhar este estado para continuar em outro aparelho">Enviar</button>
        <button class="mini-btn mini-btn-danger" type="button" data-state-delete="${slot}"${record ? '' : ' disabled'}>Apagar</button>
      </span>
    </div>`;
  };
  const rows = [rowFor('auto', '💾 Automático', false), rowFor('previous', '↩ Anterior', false)];
  if (bySlot.has('imported')) rows.push(rowFor('imported', '📥 Importado', false));
  manualSlots().forEach((slot) => rows.push(rowFor(slot, `Slot ${slot}`, true)));
  rows.push(`<div class="backup-row data-row"><span class="data-info"><small>Para continuar em outro aparelho: "Enviar" gera um arquivo pequeno; no outro EmuTab, use "Importar estado" ou solte o arquivo na página.</small></span>
    <span class="data-actions"><button class="mini-btn" type="button" data-state-import="1">📥 Importar estado</button></span></div>`);
  dom.statesList.innerHTML = rows.join('');
  dom.statesTitle.textContent = `⏪ Estados — ${session.title}`;
  dom.statesModal.classList.remove('is-hidden');
  if (!isLayerOpen('states')) layerOpened('states');
}

function closeStatesPanel() {
  if (!dom.statesModal) return;
  dom.statesModal.classList.add('is-hidden');
  layerClosed('states');
}

async function handleStatesClick(event) {
  const session = currentSession();
  if (event.target.closest('[data-state-import]')) { if (dom.stateImportInput) dom.stateImportInput.click(); return; }
  if (!session) return;
  const identity = stateIdentity(session.gameId, session.core);
  const pick = (attr) => event.target.closest(`[${attr}]`);
  const save = pick('data-state-save');
  if (save) {
    await saveSessionState(session, { kind: 'manual', slot: save.dataset.stateSave });
    openStatesPanel();
    return;
  }
  const load = pick('data-state-load');
  if (load && !load.disabled) {
    const record = await getStateRecord(stateRecordId(identity, load.dataset.stateLoad));
    if (record) { closeStatesPanel(); if (isLayerOpen('pause')) closePauseMenu(); await loadStateRecord(session, record); }
    return;
  }
  const name = pick('data-state-name');
  if (name && !name.disabled) {
    const record = await getStateRecord(stateRecordId(identity, name.dataset.stateName));
    if (!record) return;
    const text = await openPromptLayer({ title: 'Nome do slot', label: 'Para lembrar onde parou', value: record.name || '', placeholder: 'Ex.: antes do chefe da fase 4' });
    if (text !== null) { record.name = text.slice(0, 40); await putStateRecord(record); }
    openStatesPanel();
    return;
  }
  const send = pick('data-state-send');
  if (send && !send.disabled) {
    const record = await getStateRecord(stateRecordId(identity, send.dataset.stateSend));
    if (record) await exportStateRecord(record);
    return;
  }
  const del = pick('data-state-delete');
  if (del && !del.disabled) {
    if (!armButtonConfirm(del)) return;
    await deleteStateRecord(stateRecordId(identity, del.dataset.stateDelete));
    refreshSaveIndex().then(() => applyFiltersAndRender());
    updateContinueButton();
    renderResumeRow();
    openStatesPanel();
  }
}

/* --- Exportar / importar um estado isolado ------------------------------- */

function bytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(binary);
}

function base64ToBytes(text) {
  const binary = atob(String(text || ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function buildStateFile(record) {
  const payload = {
    format: 'emutab-state',
    version: 1,
    appVersion: CONFIG.appVersion,
    exportedAt: new Date().toISOString(),
    record: {
      gameId: record.gameId, title: record.title, core: record.core, build: record.build,
      slot: record.slot, name: record.name || '', savedAt: record.savedAt,
      thumbnail: record.thumbnail || null, bytes: bytesToBase64(record.bytes),
    },
  };
  const fileName = `${slugify(record.title)}-${record.slot}.emutab-state.json`;
  return new File([JSON.stringify(payload)], fileName, { type: 'application/json' });
}

async function exportStateRecord(record) {
  const file = buildStateFile(record);
  await shareOrDownloadFile(file, `Estado de ${record.title} (EmuTab)`);
}

/** Compartilha (Web Share com arquivos) ou baixa. Usado por estados e backup. */
async function shareOrDownloadFile(file, title, options = {}) {
  if (!options.downloadOnly && navigator.canShare && navigator.share) {
    try {
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title });
        return 'shared';
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return 'cancelled';
    }
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(file);
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 5000);
  return 'downloaded';
}

function isStateFile(file) {
  return Boolean(file && /\.emutab-state\.json$/i.test(file.name || ''));
}

/** Importa um arquivo de estado para o perfil ativo. */
async function importStateFile(file) {
  let payload;
  try { payload = JSON.parse(await file.text()); } catch (_) { showToast('Arquivo de estado inválido.'); return null; }
  const r = payload && payload.record;
  if (!payload || payload.format !== 'emutab-state' || !r || !r.gameId || !r.core || !r.bytes) {
    showToast('Este arquivo não é um estado do EmuTab.');
    return null;
  }
  let bytes;
  try { bytes = base64ToBytes(r.bytes); } catch (_) { showToast('Estado corrompido.'); return null; }
  const identity = stateIdentity(r.gameId, r.core);
  const existing = await listStateRecords(identity).catch(() => []);
  const used = new Set(existing.map((rec) => rec.slot));
  const slot = manualSlots().find((s) => !used.has(s)) || 'imported';
  const record = {
    id: stateRecordId(identity, slot), identity, gameId: r.gameId, title: r.title || r.gameId, core: r.core,
    build: r.build || CONFIG.emulatorBuild, slot, name: r.name || 'importado', bytes,
    thumbnail: typeof r.thumbnail === 'string' ? r.thumbnail : null,
    savedAt: Number(r.savedAt) || Date.now(), appVersion: CONFIG.appVersion, profile: state.profileId || 'main',
  };
  await putStateRecord(record);
  refreshSaveIndex().then(() => applyFiltersAndRender());
  updateContinueButton();
  renderResumeRow();
  const here = findGameById(r.gameId) || state.localRoms.has(r.gameId);
  showToast(here
    ? `Estado de "${record.title}" importado no ${describeSlot(slot)}.`
    : `Estado de "${record.title}" importado, mas este jogo não está neste aparelho (ROM local precisa ser o mesmo arquivo).`);
  if (isLayerOpen('states')) openStatesPanel();
  return record;
}

/* --- Retomar: escolha ao continuar um jogo ------------------------------ */

/** Abre o jogo oferecendo retomar do último ponto quando houver estado. */
async function startGameWithResume(game, options = {}) {
  let records = [];
  try {
    records = await listStateRecords(stateIdentity(game.id, game.core));
  } catch (_) { records = []; }
  const candidates = records.filter((r) => r.slot !== 'previous');
  if (!candidates.length || options.skipResume || options.resumeRecord) {
    return launchGame(game, options);
  }
  const best = candidates[0]; // mais recente (auto ou manual)
  const when = new Date(best.savedAt).toLocaleString('pt-BR');
  const actions = [
    { label: `Retomar (${describeSlot(best.slot, best.name)} · ${when})`, handler: () => launchGame(game, Object.assign({}, options, { resumeRecord: best })) },
  ];
  candidates.filter((r) => r !== best).slice(0, 3).forEach((record) => {
    actions.push({ label: `Carregar ${describeSlot(record.slot, record.name)} (${new Date(record.savedAt).toLocaleString('pt-BR')})`, handler: () => launchGame(game, Object.assign({}, options, { resumeRecord: record })) });
  });
  actions.push({ label: 'Começar do início', handler: () => launchGame(game, options) });
  return new Promise((resolve) => {
    openConfirmLayer({
      title: `Retomar ${game.title}?`,
      message: 'Existe progresso guardado neste navegador para este jogo.',
      actions: actions.map((a) => ({ label: a.label, handler: () => { a.handler(); resolve(); } })),
      cancelLabel: 'Cancelar',
    });
  });
}

/** Ponto único de abertura de um jogo (remoto ou local) via sessão. */
function launchGame(game, options = {}) {
  if (game.isLocal) {
    return startSession({ kind: 'local', gameId: game.id, core: game.core, title: game.title, game, options });
  }
  return startSession({ kind: 'remote', game, gameId: game.id, core: game.core, title: game.title, options });
}

// @generated-exports
export { MAX_MANUAL_SLOTS, STATES_DB, STATES_DB_VERSION, STATES_META_STORE, STATES_STORE, base64ToBytes, buildStateFile, bytesToBase64, closeStatesPanel, deleteStateRecord, deleteStatesByPrefix, describeSlot, exportStateRecord, getStateRecord, handleStatesClick, idbRequest, importStateFile, isStateFile, latestStateMeta, launchGame, listAllStateKeys, listStateRecords, loadStateRecord, manualSlots, metaFromRecord, openStatesDb, openStatesPanel, putStateRecord, quickLoadCurrent, quickSaveCurrent, rebuildMetaForIdentity, refreshStateMeta, saveSessionState, shareOrDownloadFile, startGameWithResume, stateIdentity, stateRecordId, statesTx, updateStateMetaCache };
// @generated-exports
